import React from 'react';

import { di } from 'react-magnetic-di';
import { shallowEqualObjects } from 'shallow-equal';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import {
	useSelectedPinnedItemSelector,
	useSelectedSpaceSelector,
	useUIStore,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';

import {
	type InteractiveChatHistoryControllerProps,
	InteractiveChatHistoryController,
} from './InteractiveChatHistoryController';
import {
	type ReadOnlyChatHistoryControllerProps,
	ReadOnlyChatHistoryController,
} from './ReadOnlyChatHistoryController';

type InteractiveChatHistoryProps = Omit<
	InteractiveChatHistoryControllerProps,
	'selectedPinnedItem' | 'selectedSpace'
>;

export type LiveChatHistoryProps = InteractiveChatHistoryProps & {
	interactionMode?: 'live';
};

export type ReadOnlyChatHistoryProps = ReadOnlyChatHistoryControllerProps & {
	interactionMode: 'read-only';
};

export type ChatHistoryProps = LiveChatHistoryProps | ReadOnlyChatHistoryProps;

// rovo_ttai_segment_render_stabilization: absorb parent render waves when ChatHistory props are stable.
const MemoizedChatHistory = React.memo(InteractiveChatHistoryController, shallowEqualObjects);

// rovo_ttai_segment_render_stabilization ON: subscribe to `selectedSpace` via the
// narrow selector so the component (and its memo boundary) only re-renders when
// `selectedSpace` changes, rather than on every UI-store state change.
const ChatHistoryWithSelector = (props: InteractiveChatHistoryProps): React.JSX.Element => {
	di(useSelectedSpaceSelector, useSelectedPinnedItemSelector);
	const [{ selectedSpace }] = useSelectedSpaceSelector();
	const [{ selectedPinnedItem }] = useSelectedPinnedItemSelector();
	return (
		<MemoizedChatHistory
			{...props}
			selectedSpace={selectedSpace}
			selectedPinnedItem={selectedPinnedItem}
		/>
	);
};

// rovo_ttai_segment_render_stabilization OFF/control: subscribe to the broad
// UI store, matching the prior behaviour where the component re-rendered on any
// UI-store state change.
const ChatHistoryWithBroadStore = (props: InteractiveChatHistoryProps): React.JSX.Element => {
	di(useUIStore);
	const { selectedSpace, selectedPinnedItem } = useUIStore();
	return (
		<InteractiveChatHistoryController
			{...props}
			selectedSpace={selectedSpace}
			selectedPinnedItem={selectedPinnedItem}
		/>
	);
};

const LiveChatHistory = ({
	interactionMode: _interactionMode,
	...props
}: LiveChatHistoryProps): React.JSX.Element => {
	const isRenderStabilizationEnabled =
		FeatureGates.getExperimentValue<boolean>(
			'rovo_ttai_segment_render_stabilization',
			'isEnabled',
			false,
		) === true;

	return isRenderStabilizationEnabled ? (
		<ChatHistoryWithSelector {...props} />
	) : (
		<ChatHistoryWithBroadStore {...props} />
	);
};

const ReadOnlyChatHistory = ({
	interactionMode: _interactionMode,
	...props
}: ReadOnlyChatHistoryProps): React.JSX.Element => <ReadOnlyChatHistoryController {...props} />;

export const ChatHistory = (props: ChatHistoryProps): React.JSX.Element => {
	if (props.interactionMode === 'read-only') {
		return <ReadOnlyChatHistory {...props} />;
	}

	return <LiveChatHistory {...props} />;
};
